import * as Sentry from '@sentry/react';
import { create } from 'zustand';
import { AuthService } from './auth.service';
import { identifyUser, clearUser } from '@/lib/sentry-identity';
import { registerAuthSessionHandlers, runSessionCleanup } from '@/shared/api/auth-session-handlers';
import { tokenStore } from '@/shared/api/token-store';
import type {
  LoginResponse,
  MfaChallengeResponse,
  SelectClinicResponse,
  RefreshResponse,
  CurrentUserResponse,
} from './auth.service';

const authService = new AuthService();

/** Single in-flight refresh — rotation revokes the cookie token; parallel calls must share one request. */
let refreshSessionInFlight: Promise<RefreshResponse> | null = null;

function refreshSessionOnce(): Promise<RefreshResponse> {
  if (refreshSessionInFlight) {
    return refreshSessionInFlight;
  }

  refreshSessionInFlight = authService.refresh().finally(() => {
    refreshSessionInFlight = null;
  });

  return refreshSessionInFlight;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: string;
  organizationId: string;
  clinicIds: string[];
}

export interface ClinicAssignment {
  clinicId: string;
  clinicName: string;
  status?: string;
}

export type LoginResult =
  | { kind: 'mfa_required' }
  | { kind: 'consultant_clinic_select'; clinics: ClinicAssignment[] }
  | { kind: 'complete'; user: UserProfile };

interface PersistedAuth {
  user: UserProfile;
  activeClinic?: ClinicAssignment | null;
}

export const SESSION_FLAG_KEY = 'dentxe.session';

interface AccessTokenClaims {
  sub?: string;
  organizationId?: string;
  role?: string;
  clinics?: string[];
  activeClinicId?: string;
  active_clinic_id?: string;
  clinic_id?: string;
}

type UserLike = Partial<UserProfile> & {
  displayName?: string;
  fullName?: string;
};

interface ClinicProfileRow {
  id: string;
  displayName: string;
  status: string;
}

export interface CurrentUserApiResponse {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  organizationId: string;
  clinicIds: string[];
  activeClinicId: string | null;
  clinics: ClinicProfileRow[];
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: boolean;
  mfaSessionToken: string | null;
  user: UserProfile | null;
  isMfaEnrolled: boolean;
  consultantRefreshToken: boolean;
  assignedClinics: ClinicAssignment[];
  activeClinic: ClinicAssignment | null;
  isSwitchingClinic: boolean;

  login: (email: string, password: string) => Promise<LoginResult>;
  completeMfa: (code: string) => Promise<void>;
  selectClinic: (clinicId: string) => Promise<void>;
  syncClinicsFromProfile: (profile: CurrentUserApiResponse) => void;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  silentRefresh: () => Promise<void>;
  loadCurrentUser: () => Promise<void>;
  setTokens: (accessToken: string) => void;
  hydrateFromStorage: () => void;
}

const AUTH_STORAGE_KEY = 'dentxe.auth';

function mapLoginErrorMessage(rawMessage: unknown): string {
  if (typeof rawMessage !== 'string' || rawMessage.trim().length === 0) {
    return 'Unable to sign in. Please try again.';
  }

  if (rawMessage === 'INVALID_CREDENTIALS') {
    return 'Incorrect email or password. Please try again.';
  }

  return rawMessage;
}

function setSessionFlag(user: UserProfile | null): void {
  if (user) {
    localStorage.setItem(SESSION_FLAG_KEY, JSON.stringify({ loggedIn: true, userId: user.id }));
  } else {
    localStorage.removeItem(SESSION_FLAG_KEY);
  }
}

function getLoggedOutState(): Pick<
  AuthState,
  | 'accessToken'
  | 'refreshToken'
  | 'mfaSessionToken'
  | 'user'
  | 'isMfaEnrolled'
  | 'consultantRefreshToken'
  | 'assignedClinics'
  | 'activeClinic'
  | 'isSwitchingClinic'
> {
  return {
    accessToken: null,
    refreshToken: false,
    mfaSessionToken: null,
    user: null,
    isMfaEnrolled: false,
    consultantRefreshToken: false,
    assignedClinics: [],
    activeClinic: null,
    isSwitchingClinic: false,
  };
}

function parseJwtClaims(accessToken: string): AccessTokenClaims {
  try {
    const [, payload] = accessToken.split('.');
    if (!payload) return {};
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = JSON.parse(atob(padded)) as AccessTokenClaims;
    return decoded;
  } catch {
    return {};
  }
}

function parseActiveClinicIdFromToken(accessToken: string): string | null {
  const claims = parseJwtClaims(accessToken);
  return claims.activeClinicId ?? claims.active_clinic_id ?? claims.clinic_id ?? null;
}

function toDisplayNameFromEmail(email: string): string {
  const localPart = email.split('@')[0] ?? email;
  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function resolveUserProfile(params: {
  data: unknown;
  accessToken: string;
  fallbackUser: UserProfile | null;
  fallbackEmail?: string;
}): UserProfile {
  const { data, accessToken, fallbackUser, fallbackEmail } = params;
  const claims = parseJwtClaims(accessToken);
  const record = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const userFromPayload = (
    record['user'] && typeof record['user'] === 'object' ? record['user'] : record
  ) as UserLike;

  const email = userFromPayload.email ?? fallbackEmail ?? fallbackUser?.email ?? '';
  const name =
    userFromPayload.name ??
    userFromPayload.displayName ??
    userFromPayload.fullName ??
    fallbackUser?.name ??
    (email ? toDisplayNameFromEmail(email) : 'User');
  const role = userFromPayload.role ?? claims.role ?? fallbackUser?.role ?? 'org_user';
  const organizationId =
    userFromPayload.organizationId ?? claims.organizationId ?? fallbackUser?.organizationId ?? '';
  const clinicIds = userFromPayload.clinicIds ?? claims.clinics ?? fallbackUser?.clinicIds ?? [];
  const id = userFromPayload.id ?? claims.sub ?? fallbackUser?.id ?? '';

  return {
    id,
    email,
    name,
    role,
    organizationId,
    clinicIds,
  };
}

function mapClinicsToAssignments(clinics: ClinicProfileRow[]): ClinicAssignment[] {
  return clinics.map((clinic) => ({
    clinicId: clinic.id,
    clinicName: clinic.displayName,
    status: clinic.status,
  }));
}

function resolveActiveClinic(
  assignments: ClinicAssignment[],
  activeClinicId: string | null | undefined,
  fallback: ClinicAssignment | null,
): ClinicAssignment | null {
  if (!activeClinicId) {
    return fallback;
  }

  return assignments.find((clinic) => clinic.clinicId === activeClinicId) ?? fallback;
}

function mapCurrentUserToProfile(currentUser: CurrentUserApiResponse): UserProfile {
  return {
    id: currentUser.id,
    email: currentUser.email,
    name: currentUser.displayName?.trim() || toDisplayNameFromEmail(currentUser.email),
    role: currentUser.role,
    organizationId: currentUser.organizationId,
    clinicIds: currentUser.clinicIds,
  };
}

function clinicAssignmentsEqual(a: ClinicAssignment[], b: ClinicAssignment[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every(
    (clinic, index) =>
      clinic.clinicId === b[index]?.clinicId &&
      clinic.clinicName === b[index]?.clinicName &&
      clinic.status === b[index]?.status,
  );
}

function activeClinicEqual(
  a: ClinicAssignment | null,
  b: ClinicAssignment | null,
): boolean {
  if (a === b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  return a.clinicId === b.clinicId && a.clinicName === b.clinicName && a.status === b.status;
}

function userProfileEqual(a: UserProfile, b: UserProfile): boolean {
  return (
    a.id === b.id &&
    a.email === b.email &&
    a.name === b.name &&
    a.role === b.role &&
    a.organizationId === b.organizationId &&
    a.clinicIds.length === b.clinicIds.length &&
    a.clinicIds.every((id, index) => id === b.clinicIds[index])
  );
}

function profileAuthStateUnchanged(
  current: Pick<AuthState, 'user' | 'assignedClinics' | 'activeClinic'>,
  next: Pick<AuthState, 'user' | 'assignedClinics' | 'activeClinic'>,
): boolean {
  if (!current.user || !next.user) {
    return false;
  }

  return (
    userProfileEqual(current.user, next.user) &&
    clinicAssignmentsEqual(current.assignedClinics, next.assignedClinics) &&
    activeClinicEqual(current.activeClinic, next.activeClinic)
  );
}

function applyProfileToAuthState(
  accessToken: string,
  profile: CurrentUserApiResponse,
  previousActiveClinic: ClinicAssignment | null,
): Pick<AuthState, 'user' | 'assignedClinics' | 'activeClinic'> {
  const assignedClinics = mapClinicsToAssignments(profile.clinics);
  const activeClinic = resolveActiveClinic(
    assignedClinics,
    profile.activeClinicId ?? parseActiveClinicIdFromToken(accessToken),
    previousActiveClinic,
  );

  return {
    user: mapCurrentUserToProfile(profile),
    assignedClinics,
    activeClinic,
  };
}

function persistAuth(user: UserProfile, activeClinic: ClinicAssignment | null): void {
  const payload: PersistedAuth = { user, activeClinic };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
}

function clearPersistedAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  refreshToken: false,
  mfaSessionToken: null,
  user: null,
  isMfaEnrolled: false,
  consultantRefreshToken: false,
  assignedClinics: [],
  activeClinic: null,
  isSwitchingClinic: false,

  login: async (email, password) => {
    let data: LoginResponse;
    try {
      data = await authService.login(email, password);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to sign in. Please try again.';
      throw new Error(mapLoginErrorMessage(message), { cause: err });
    }

    if (data.mfaSessionToken) {
      set({ mfaSessionToken: data.mfaSessionToken });
      if (data.assignedClinics) {
        return {
          kind: 'consultant_clinic_select',
          clinics: data.assignedClinics,
        };
      }
      return { kind: 'mfa_required' };
    }

    const token = data.accessToken;
    if (!token) throw new Error('Login succeeded but no access token received');

    const resolvedUser = resolveUserProfile({
      data,
      accessToken: token,
      fallbackUser: get().user,
      fallbackEmail: email,
    });

    tokenStore.set(token);
    set({
      accessToken: token,
      refreshToken: true,
      user: resolvedUser,
      isMfaEnrolled: true,
    });
    persistAuth(resolvedUser, get().activeClinic);
    setSessionFlag(resolvedUser);
    await get().loadCurrentUser();
    // SENTRY: identify user after successful login
    {
      const { user, activeClinic } = get();
      if (user) {
        identifyUser({
          id: user.id,
          name: user.name,
          role: user.role,
          clinicId: activeClinic?.clinicId,
          clinicName: activeClinic?.clinicName,
        });
      }
    }

    return { kind: 'complete', user: get().user ?? resolvedUser };
  },

  completeMfa: async (code) => {
    const { mfaSessionToken } = get();
    if (!mfaSessionToken) throw new Error('No MFA session token');

    let data: MfaChallengeResponse;
    try {
      data = await authService.completeMfa(code, mfaSessionToken);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'MFA challenge failed';
      throw new Error(message, { cause: err });
    }

    const resolvedUser = resolveUserProfile({
      data,
      accessToken: data.accessToken,
      fallbackUser: get().user,
    });

    tokenStore.set(data.accessToken);
    set({
      accessToken: data.accessToken,
      refreshToken: true,
      user: resolvedUser,
      isMfaEnrolled: true,
      mfaSessionToken: null,
    });
    persistAuth(resolvedUser, get().activeClinic);
    setSessionFlag(resolvedUser);
    await get().loadCurrentUser();
    // SENTRY: identify user after MFA challenge
    {
      const { user, activeClinic } = get();
      if (user) {
        identifyUser({
          id: user.id,
          name: user.name,
          role: user.role,
          clinicId: activeClinic?.clinicId,
          clinicName: activeClinic?.clinicName,
        });
      }
    }
  },

  selectClinic: async (clinicId) => {
    const { accessToken, assignedClinics, activeClinic } = get();
    if (!accessToken) {
      throw new Error('Not authenticated');
    }
    if (clinicId === activeClinic?.clinicId) {
      return;
    }

    set({ isSwitchingClinic: true });
    try {
      const data: SelectClinicResponse = await authService.selectClinic(clinicId);

      const selectedClinic: ClinicAssignment = {
        clinicId: data.clinicId ?? clinicId,
        clinicName:
          data.clinicName ??
          assignedClinics.find((clinic) => clinic.clinicId === clinicId)?.clinicName ??
          'Clinic',
      };

      tokenStore.set(data.accessToken);
      set({
        accessToken: data.accessToken,
        consultantRefreshToken: true,
        activeClinic: selectedClinic,
      });

      const user = get().user;
      if (user) {
        setSessionFlag(user);
        persistAuth(user, selectedClinic);
      }

      await get().loadCurrentUser();
      // SENTRY: identify user after clinic switch
      {
        const { user, activeClinic } = get();
        if (user) {
          identifyUser({
            id: user.id,
            name: user.name,
            role: user.role,
            clinicId: activeClinic?.clinicId,
            clinicName: activeClinic?.clinicName,
          });
        }
      }
    } finally {
      set({ isSwitchingClinic: false });
    }
  },

  syncClinicsFromProfile: (profile) => {
    const state = get();
    if (!state.accessToken) {
      return;
    }

    const next = applyProfileToAuthState(state.accessToken, profile, state.activeClinic);
    if (profileAuthStateUnchanged(state, next)) {
      return;
    }

    set(next);

    if (next.user) {
      setSessionFlag(next.user);
      persistAuth(next.user, next.activeClinic);
    }
  },

  logout: () => {
    runSessionCleanup();
    // SENTRY: clear user on logout
    clearUser();
    tokenStore.set(null);
    clearPersistedAuth();
    localStorage.removeItem(SESSION_FLAG_KEY);
    set(getLoggedOutState());

    authService.logout().catch((err: unknown) => {
      Sentry.captureException(err);
    });
  },

  refreshAccessToken: async () => {
    let data: RefreshResponse;
    try {
      data = await refreshSessionOnce();
    } catch (err) {
      // SENTRY: capture refresh failure before clearing
      Sentry.captureException(err);
      // SENTRY: clear user on session expiry
      clearUser();
      get().logout();
      window.location.href = '/login?reason=token_expired';
      return;
    }

    tokenStore.set(data.accessToken);
    set({
      accessToken: data.accessToken,
      refreshToken: true,
    });
    await get().loadCurrentUser();
  },

  silentRefresh: async () => {
    try {
      const data = await refreshSessionOnce();
      tokenStore.set(data.accessToken);
      set({
        accessToken: data.accessToken,
        refreshToken: true,
      });
    } catch {
      // Another caller may have completed refreshSessionOnce() first (e.g. StrictMode double-mount).
      if (get().accessToken) {
        return;
      }
      get().logout();
    }
  },

  loadCurrentUser: async () => {
    const { activeClinic } = get();

    try {
      const data: CurrentUserResponse = await authService.getCurrentUser();
      const accessToken = get().accessToken;
      if (!accessToken) return;
      const next = applyProfileToAuthState(accessToken, data, activeClinic);
      set(next);
      if (next.user) {
        setSessionFlag(next.user);
        persistAuth(next.user, next.activeClinic);
      }
      // SENTRY: identify user after profile refresh
      {
        const { user, activeClinic } = get();
        if (user) {
          identifyUser({
            id: user.id,
            name: user.name,
            role: user.role,
            clinicId: activeClinic?.clinicId,
            clinicName: activeClinic?.clinicName,
          });
        }
      }
    } catch (err) {
      Sentry.captureException(err);
      const { user, activeClinic } = get();
      if (user) {
        persistAuth(user, activeClinic);
      }
    }
  },

  setTokens: (accessToken) => {
    const resolvedUser = resolveUserProfile({
      data: {},
      accessToken,
      fallbackUser: get().user,
    });
    tokenStore.set(accessToken);
    set({ accessToken, refreshToken: true, user: resolvedUser });
    void get().loadCurrentUser();
  },

  hydrateFromStorage: () => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!raw) {
        set(getLoggedOutState());
        return;
      }
      const parsed = JSON.parse(raw) as PersistedAuth;
      if (parsed.user) {
        set({
          refreshToken: false,
          user: parsed.user,
          isMfaEnrolled: true,
          activeClinic: parsed.activeClinic ?? null,
        });
        // SENTRY: identify user after session restore from storage
        {
          const { user, activeClinic } = get();
          if (user) {
            identifyUser({
              id: user.id,
              name: user.name,
              role: user.role,
              clinicId: activeClinic?.clinicId,
              clinicName: activeClinic?.clinicName,
            });
          }
        }
        return;
      }

      set(getLoggedOutState());
    } catch (err) {
      Sentry.captureException(err);
      clearPersistedAuth();
      set(getLoggedOutState());
    }
  },
}));

registerAuthSessionHandlers({
  refreshAccessToken: () => useAuthStore.getState().refreshAccessToken(),
  logout: () => useAuthStore.getState().logout(),
  getActiveClinic: () => useAuthStore.getState().activeClinic,
});
